import { Link } from "react-router-dom";

import { EquipmentCard } from "./EquipmentCard";
import { usePublicCategory } from "./api";
import type { TypeDetail } from "./types";

// Three sibling cards from the same category, surfaced below the fold
// so customers can comparison-shop without back-buttoning. Quiet failure
// — if the sibling fetch errors or the type is the only one in its
// category, the rail collapses (no skeleton hangs around forever).
export interface SimilarEquipmentRailProps {
  type: TypeDetail;
  limit?: number;
}

export function SimilarEquipmentRail({
  type,
  limit = 3,
}: SimilarEquipmentRailProps): JSX.Element | null {
  const { data } = usePublicCategory(type.category.slug);

  if (!data) return null;
  const siblings = data.types
    .filter((t) => t.slug !== type.slug)
    .slice(0, limit);
  if (siblings.length === 0) return null;

  return (
    <section className="mt-12" aria-labelledby="similar-heading">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2
            id="similar-heading"
            className="font-serif text-xl font-bold italic tracking-tight text-brand-black md:text-2xl"
          >
            Compare to similar
          </h2>
          <span aria-hidden="true" className="mt-2 inline-block h-0.5 w-10 bg-brand-red" />
        </div>
        <Link
          to={`/c/${type.category.slug}`}
          className="hidden text-sm font-semibold text-brand-red hover:underline sm:inline"
        >
          See all in {type.category.name} →
        </Link>
      </div>

      <ul className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        {siblings.map((sib) => (
          <li key={sib.id}>
            <EquipmentCard type={sib} categorySlug={type.category.slug} />
          </li>
        ))}
      </ul>
    </section>
  );
}
